import * as THREE from 'three';
import type { SketchStyleSplineObject } from '../../../mesh/sketch/SketchStyleSplineObject';
import type { ProjectionResult } from '../model/ProjectionResult';
import type {
	ReferenceDrivenStyleSplineRelationRuntimeRecord,
	SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import { resolveSourceRefEntityId } from '../model/SketchEntityReferenceRecord';
import type { ExecutionDiagnostic } from '../model/SketchEntityReferenceDiagnostics';
import { BaseReferenceDrivenRelationService } from './BaseReferenceDrivenRelationService';
import { ReferenceDrivenStyleSplineOnCurveConstraint } from './constraints/ReferenceDrivenStyleSplineOnCurveConstraint';
import type {
	ReferenceDrivenStyleSplineRelationBinding,
	ReferenceDrivenStyleSplineRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenStyleSplineRelationService';

export type {
	ReferenceDrivenStyleSplineRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenStyleSplineRelationService';

/**
 * 参考驱动样式样条关系服务。
 */
export class ReferenceDrivenStyleSplineRelationService
	extends BaseReferenceDrivenRelationService<
		ReferenceDrivenStyleSplineRelationRuntimeRecord,
		ReferenceDrivenStyleSplineRelationBinding
	> {
	/**
	 * 挂载样式样条 on-curve 约束。
	 *
	 * @param input 挂载输入。
	 * @returns 挂载结果。
	 */
	public attach(input: {
		reference: SketchEntityReferenceRecord;
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'style-spline' }>;
		targetSpline: SketchStyleSplineObject;
		runtimeSourceName?: string;
	}): ReferenceDrivenStyleSplineRelationAttachResult {
		this.detach({
			referenceId: input.reference.referenceId,
			targetSpline: input.targetSpline,
		});

		const record: ReferenceDrivenStyleSplineRelationRuntimeRecord = {
			referenceId: input.reference.referenceId,
			relationKind: 'on-curve',
			driveMode: 'projection-drives-target',
			sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
			targetEntityId: input.targetSpline.persistentId,
			constraintId: -1,
		};
		const constraint = new ReferenceDrivenStyleSplineOnCurveConstraint({
			referenceId: input.reference.referenceId,
			projectedControlPointWorlds: input.projectionResult.geometryPayload.controlPointWorlds
				.map((point) => new THREE.Vector3(...point)),
			projectedCurvePointWorlds: input.projectionResult.geometryPayload.curvePointWorlds
				.map((point) => new THREE.Vector3(...point)),
			projectedCurveMetadata: input.projectionResult.geometryPayload.curveMetadata,
			sourceDisplayLabel: input.reference.sourceDisplayLabel,
			runtimeSourceName: input.runtimeSourceName,
		});
		input.targetSpline.constraints.addRelation(constraint);
		record.constraintId = constraint.id;
		this.setBinding(input.reference.referenceId, {
			constraint,
			record,
			targetSpline: input.targetSpline,
		});

		return {
			record,
			diagnostics: [],
		};
	}

	/**
	 * 拆除样式样条关系。
	 *
	 * @param input 拆除输入。
	 * @returns 诊断信息。
	 */
	public detach(input: {
		referenceId: string;
		targetSpline?: SketchStyleSplineObject;
	}): ExecutionDiagnostic[] {
		const binding = this.getBinding(input.referenceId);
		if (binding) {
			binding.targetSpline.constraints.removeRelation(binding.record.constraintId);
			this.deleteBinding(input.referenceId);
			return [];
		}

		if (input.targetSpline) {
			const constraint = input.targetSpline.constraints.getRelations().find((candidate) => (
				candidate instanceof ReferenceDrivenStyleSplineOnCurveConstraint
				&& candidate.referenceId === input.referenceId
			));
			if (constraint) {
				input.targetSpline.constraints.removeRelation(constraint.id);
			}
		}
		return [];
	}
}
